import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Upload, Trash2, Loader2, Send, FileText, AlertTriangle, Plus } from 'lucide-react';
import type { Customer } from '@ach/shared';
import { formatUsd, parseAmountToCents, sumCents } from '@ach/shared';

import { api, ApiError } from '../lib/api.js';
import { Field, ErrorNote, WarningNote, InfoNote, ParseBadge } from './components.js';

interface ParsedResult {
  status: 'parsed' | 'failed' | 'manual';
  invoiceNumber: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  amountCents: number | null;
  poNumber: string | null;
  warnings: string[];
  totals: Record<string, number | string | null>;
}

interface UploadResult {
  storagePath: string;
  originalFilename: string;
  byteSize: number;
  sha256: string;
  parsed: ParsedResult;
}

/** One editable invoice row on the draft. */
interface Row {
  key: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  /** As typed, so the admin sees exactly what they entered. */
  amount: string;
  poNumber: string;
  storagePath: string | null;
  originalFilename: string | null;
  byteSize: number | null;
  sha256: string | null;
  parseStatus: 'parsed' | 'manual' | 'failed';
  parsedJson: unknown;
  warnings: string[];
}

let rowCounter = 0;
const blankRow = (): Row => ({
  key: `row-${(rowCounter += 1)}`,
  invoiceNumber: '',
  invoiceDate: '',
  dueDate: '',
  amount: '',
  poNumber: '',
  storagePath: null,
  originalFilename: null,
  byteSize: null,
  sha256: null,
  parseStatus: 'manual',
  parsedJson: null,
  warnings: [],
});

export function RequestNew() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState(params.get('customer') ?? '');
  const [customerSearch, setCustomerSearch] = useState('');
  const [rows, setRows] = useState<Row[]>([blankRow()]);
  const [note, setNote] = useState('');
  const [verificationKind, setVerificationKind] = useState<'total' | 'zip'>('total');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      api
        .get<{ customers: Customer[] }>(
          `/api/admin/customers${customerSearch ? `?q=${encodeURIComponent(customerSearch)}` : ''}`,
        )
        .then((data) => setCustomers(data.customers))
        .catch(() => setCustomers([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  const customer = customers.find((c) => c.id === customerId);
  const total = sumCents(rows.map((row) => parseAmountToCents(row.amount) ?? 0));

  // ZIP verification needs a ZIP on file. Surfacing it here beats a rejection
  // at send time, after the whole request has been built.
  const zipUnavailable = verificationKind === 'zip' && customer !== undefined && !customer.postalCode;

  function updateRow(key: string, patch: Partial<Row>) {
    setRows((previous) => previous.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  /**
   * Uploads and parses, then fills a row.
   *
   * Uses fetch directly rather than the api helper because this is multipart —
   * setting a JSON content-type here would stop multer finding the boundary.
   */
  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);

    try {
      for (const file of Array.from(files)) {
        const body = new FormData();
        body.append('file', file);

        const response = await fetch('/api/admin/invoices/parse', {
          method: 'POST',
          // No auth header: the admin session is an httpOnly cookie, which
          // rides along with credentials. No content-type either — the browser
          // sets the multipart boundary, and overriding it breaks the parse.
          credentials: 'same-origin',
          body,
        });

        if (!response.ok) {
          const detail = (await response.json().catch(() => ({}))) as { error?: string };
          throw new Error(detail.error ?? `Upload failed (${response.status}).`);
        }

        const result = (await response.json()) as UploadResult;
        const parsed = result.parsed;

        const row: Row = {
          ...blankRow(),
          invoiceNumber: parsed.invoiceNumber ?? '',
          invoiceDate: parsed.invoiceDate ?? '',
          dueDate: parsed.dueDate ?? '',
          // Only prefilled when the parse reconciled. A failed parse leaves
          // this blank on purpose — a guessed amount becomes a wrong debit.
          amount: parsed.amountCents !== null ? (parsed.amountCents / 100).toFixed(2) : '',
          poNumber: parsed.poNumber ?? '',
          storagePath: result.storagePath,
          originalFilename: result.originalFilename,
          byteSize: result.byteSize,
          sha256: result.sha256,
                  parseStatus: parsed.status,
          parsedJson: parsed,
          warnings: parsed.warnings,
        };

        setRows((previous) => {
          // Fill the first empty row rather than appending below it.
          const emptyIndex = previous.findIndex(
            (r) => r.invoiceNumber === '' && r.amount === '' && r.storagePath === null,
          );
          if (emptyIndex === -1) return [...previous, row];
          const next = [...previous];
          next[emptyIndex] = row;
          return next;
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not upload that file.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function createAndSend(send: boolean) {
    setSubmitting(true);
    setError(null);

    const invoices = rows
      .filter((row) => row.invoiceNumber.trim() !== '' || row.amount.trim() !== '')
      .map((row) => ({
        invoiceNumber: row.invoiceNumber.trim(),
        invoiceDate: row.invoiceDate || null,
        dueDate: row.dueDate || null,
        amountCents: parseAmountToCents(row.amount) ?? 0,
        poNumber: row.poNumber || null,
        description: null,
        storagePath: row.storagePath,
        originalFilename: row.originalFilename,
        byteSize: row.byteSize,
        sha256: row.sha256,
        parseStatus: row.parseStatus,
        parsedJson: row.parsedJson,
      }));

    if (invoices.length === 0) {
      setError('Add at least one invoice.');
      setSubmitting(false);
      return;
    }
    const bad = invoices.find((invoice) => invoice.amountCents <= 0);
    if (bad) {
      setError(`Enter a valid amount for invoice ${bad.invoiceNumber || '(unnumbered)'}.`);
      setSubmitting(false);
      return;
    }

    try {
      const { request } = await api.post<{ request: { id: string } }>(
        '/api/admin/payment-requests',
        { customerId, invoices, noteToCustomer: note || null, verificationKind },
      );

      if (send) {
        const result = await api.post<{ sent: boolean; error: string | null }>(
          `/api/admin/payment-requests/${request.id}/send`,
        );
        if (!result.sent) {
          // The request exists and the token is live; only the email failed.
          // Going to the detail page lets them retry from there rather than
          // losing the work.
          navigate(`/admin/requests/${request.id}?emailFailed=1`);
          return;
        }
      }
      navigate(`/admin/requests/${request.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not create the request.');
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-900">New payment request</h1>

      {error && <ErrorNote>{error}</ErrorNote>}

      <section className="card space-y-4 p-6">
        <h2 className="font-semibold text-slate-900">Customer</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Search">
            <input
              className="field-input"
              placeholder="Name, company or email"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
            />
          </Field>
          <Field label="Customer" required>
            <select
              className="field-input"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
            >
              <option value="">Choose a customer…</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.company ? ` — ${c.company}` : ''} ({c.email})
                </option>
              ))}
            </select>
          </Field>
        </div>
        {customerId === '' && (
          <p className="text-sm text-slate-500">
            Not there?{' '}
            <Link to="/admin/customers/new" className="text-gold-700 underline">
              Add a customer
            </Link>
            .
          </p>
        )}
      </section>

      <section className="card space-y-4 p-6">
        <div className="flex items-center gap-3">
          <h2 className="font-semibold text-slate-900">Invoices</h2>
          <div className="ml-auto flex items-center gap-2">
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              className="hidden"
              onChange={(e) => void handleFiles(e.target.files)}
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Upload className="h-4 w-4" aria-hidden />
              )}
              Upload invoice PDFs
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setRows((p) => [...p, blankRow()])}
            >
              <Plus className="h-4 w-4" aria-hidden />
              Add row
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {rows.map((row) => (
            <div key={row.key} className="rounded-lg border border-slate-200 p-4">
              <div className="grid gap-3 sm:grid-cols-12">
                <div className="sm:col-span-3">
                  <Field label="Invoice number" required>
                    <input
                      className="field-input"
                      value={row.invoiceNumber}
                      onChange={(e) => updateRow(row.key, { invoiceNumber: e.target.value })}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Invoice date">
                    <input
                      type="date"
                      className="field-input"
                      value={row.invoiceDate}
                      onChange={(e) => updateRow(row.key, { invoiceDate: e.target.value })}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Due date">
                    <input
                      type="date"
                      className="field-input"
                      value={row.dueDate}
                      onChange={(e) => updateRow(row.key, { dueDate: e.target.value })}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="PO number">
                    <input
                      className="field-input"
                      value={row.poNumber}
                      onChange={(e) => updateRow(row.key, { poNumber: e.target.value })}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Amount" required>
                    <input
                      className="field-input text-right font-account"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={row.amount}
                      onChange={(e) =>
                        updateRow(row.key, {
                          amount: e.target.value,
                          // Typing over a parsed amount makes it a typed
                          // amount, and the badge must say so.
                          parseStatus: row.parseStatus === 'parsed' ? 'manual' : row.parseStatus,
                        })
                      }
                    />
                  </Field>
                </div>
                <div className="flex items-end justify-end sm:col-span-1">
                  <button
                    type="button"
                    className="rounded p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                    onClick={() => setRows((p) => (p.length === 1 ? [blankRow()] : p.filter((r) => r.key !== row.key)))}
                    title="Remove this invoice"
                  >
                    <Trash2 className="h-4 w-4" aria-label="Remove" />
                  </button>
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <ParseBadge status={row.parseStatus} />
                {row.originalFilename && (
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <FileText className="h-3.5 w-3.5" aria-hidden />
                    {row.storagePath ? (
                      <a
                        href={`/api/admin/invoices/file?path=${encodeURIComponent(row.storagePath)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="underline hover:text-slate-700"
                      >
                        {row.originalFilename}
                      </a>
                    ) : (
                      row.originalFilename
                    )}
                  </span>
                )}
              </div>

              {row.warnings.length > 0 && (
                <div className="mt-2 space-y-1">
                  {row.warnings.map((warning) => (
                    <p
                      key={warning}
                      className="flex items-start gap-1.5 text-xs text-amber-800"
                    >
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                      {warning}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4">
          <span className="text-sm text-slate-500">Total the customer will authorize</span>
          <span className="text-lg font-bold text-slate-900">{formatUsd(total)}</span>
        </div>
      </section>

      <section className="card space-y-4 p-6">
        <h2 className="font-semibold text-slate-900">Email</h2>

        <Field
          label="Security question"
          hint="The customer confirms this before the invoice or the bank form is shown, so a forwarded email does not expose either."
        >
          <select
            className="field-input"
            value={verificationKind}
            onChange={(e) => setVerificationKind(e.target.value as 'total' | 'zip')}
          >
            <option value="total">The invoice total ({formatUsd(total)})</option>
            <option value="zip">The billing ZIP code</option>
          </select>
        </Field>

        {zipUnavailable && (
          <WarningNote>
            {customer?.name} has no ZIP code on file, so it cannot be the security question.{' '}
            <Link to={`/admin/customers/${customerId}`} className="underline">
              Add one
            </Link>{' '}
            or verify by invoice total instead.
          </WarningNote>
        )}

        <Field label="Note to the customer" hint="Optional. Appears above the Pay Invoice button.">
          <textarea
            className="field-input"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>

        <InfoNote>
          The email carries no bank details and never asks for any by reply — it links to the secure
          form. Nothing is debited when the customer submits; you schedule that at the bank
          afterwards.
        </InfoNote>
      </section>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => void createAndSend(false)}
          disabled={submitting || customerId === ''}
        >
          Save as draft
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={() => void createAndSend(true)}
          disabled={submitting || customerId === '' || total <= 0 || zipUnavailable}
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Send className="h-4 w-4" aria-hidden />
          )}
          Send to customer
        </button>
      </div>
    </div>
  );
}
